import type { PropsWithChildren } from 'react';
import { useEffect, createContext, useState } from 'react';

import { Experiment } from '@amplitude/experiment-js-client';

const API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_EXPERIMENTS_SDK_KEY;

const experiment = Experiment.initialize(`${API_KEY}`, {
  fetchOnStart: false,
  exposureTrackingProvider: {
    track: exposure => {
      const analytics = window && window.analytics;
      analytics?.track('$exposure', exposure);
    },
  },
});

/**
 * Define your experiment key, variants, and audience attributes here!
 *
 * Dont export this. Intellisense is provided by the types. Just use the string directly.
 * Why? Because this object could get large over time and is not code-splittable.
 */
const EXPERIMENTS = {
  // `example_experiment` is the Key/ID of the experiment.
  example_experiment: {
    // The variants that the experimet can return.
    // NOTE null is returned if the user doesn't qualify for the audience.
    variants: ['example_feature', 'control'],
    // The attibutes used by the audience filter that determines if the user should be bucketed in the experiment.
    audienceAttributes: ['facility_code'],
  },
  // Live experiments:
  'app-adoption-first-order': {
    variants: ['show-app-adoption-page', 'control'],
    audienceAttributes: ['is_login_set', 'facility_code', 'operating_system', 'browser_type'],
  },
  mcyp_transfer_form_ux_updates: {
    variants: ['show_experience', 'control'],
    audienceAttributes: ['facility_zip'],
  },
  mcyp_transfer_form_intro_show_experience: {
    variants: ['show_experience', 'control'],
    audienceAttributes: ['facility_code'],
  },
  // Order status page MCYP experiment
  mcyp_show_experience: {
    variants: ['show_experience', 'control'],
    audienceAttributes: ['has_ever_transferred', 'orders_placed', 'order_status'],
  },
  streamline_cta: {
    variants: ['on', 'off'],
    audienceAttributes: ['created_days_ago'],
  },
  mcyp_transfer_form_full_profile_transfer_in: {
    variants: ['show_experience', 'control'],
    audienceAttributes: ['facility_zip'],
  },
  mcyp_pots_interstitials_only: {
    variants: ['interstitial_bring_all_meds', 'interstitial_complete_checklist'],
    audienceAttributes: ['acquisition_source', 'orders_placed', 'take_other_meds', 'has_ever_transferred'],
  },
  update_health_profile_experiment: {
    variants: ['show_experience', 'control'],
    audienceAttributes: [],
  },
  'rxlive-form-gating': {
    variants: ['on', 'off'],
    audienceAttributes: [],
  },
  should_not_require_signature: {
    variants: ['on', 'off'],
    audienceAttributes: [],
  },
  app_adoption: {
    variants: ['variation_1', 'variation_2', 'control'],
    audienceAttributes: [],
  },
  upsell_essentials_v2: {
    variants: ['existing_ui', 'variation_a_ui'],
    audienceAttributes: [],
  },
  upsell_essentials_12_21_23: {
    variants: ['on', 'off'],
    audienceAttributes: ['facility_code'],
  },
  maintenance_mode: {
    variants: ['on', 'off'],
    audienceAttributes: [],
  },
} as const;

type ExperimentType = typeof EXPERIMENTS;

type ExperimentNameUnion = keyof ExperimentType;

export type ExperimentVariantUnion<K extends ExperimentNameUnion> =
  ExperimentType[K]['variants'] extends readonly (infer V)[] ? V | null : never;

type AttributeUnion<K extends ExperimentNameUnion> = 'audienceAttributes' extends keyof ExperimentType[K]
  ? ExperimentType[K]['audienceAttributes'] extends ReadonlyArray<infer A>
    ? A extends string
      ? A
      : never
    : never
  : never;

type ExperimentParams<K extends ExperimentNameUnion> = {
  experimentKey: K;
  userId: string;
  audienceAttributes?: Record<AttributeUnion<K>, any> | undefined;
};

type ExperimentContextType = {
  isStarted: boolean;
  fetchVariant: <K extends keyof ExperimentType>(
    params: ExperimentParams<K>
  ) => Promise<ExperimentVariantUnion<K> | null>;
  getEvaluatedVariant: <K extends keyof ExperimentType>(experimentKey: K) => ExperimentVariantUnion<K> | null;
};

export const ExperimentsContext = createContext<ExperimentContextType>({} as ExperimentContextType);

const ExperimentsContextProvider = ({ children }: PropsWithChildren<unknown>) => {
  const [isStarted, setIsStarted] = useState(false);

  useEffect(() => {
    const startClient = async () => {
      await experiment.start();
      setIsStarted(true);
    };
    startClient();
  }, []);

  /**
   * Get a variant for an experiment.
   * @param params
   *   experimentKey - Key for the experiment.
   *   userId - Identifier for the user. Currently, the account id in string is used as the main identifier in Amplitude.
   *   audienceAttributes - Used to determine if the user should be bucketed in the experiment.
   * @returns Promise of a variant string. Additionally, it could return a `null` for the following reasons:
   *   1. If the user does not qualify for the experiment because they didn't meet the audience requirement.
   *   2. If the client fails for some reason as if they didn't qualify for the experiment.
   *      - This is to avoid blocking the user from accessing our website.
   *   3. Experiment is turned off.
   */
  const fetchVariant = async <K extends keyof ExperimentType>(
    params: ExperimentParams<K>
  ): Promise<ExperimentVariantUnion<K> | null> => {
    try {
      if (!isStarted) {
        await experiment.start();
        setIsStarted(true);
      }

      const amplitudeUser = {
        user_id: params.userId,
        user_properties: params.audienceAttributes,
      };

      const variant = (await experiment.fetch(amplitudeUser, { flagKeys: [params.experimentKey] })).variant(
        params.experimentKey
      );
      console.info(`Experiment ${params.experimentKey} variant:`, variant);

      if (variant.value === undefined) {
        console.info(`Experiment ${params.experimentKey} cannot be found or turned off`);
        return null;
      }

      console.info('Experiment variant.value:', variant.value);

      return variant.value as ExperimentVariantUnion<K>;
    } catch (error) {
      console.error('Failed to start or get experiment variant', { error });
      /**
       * If client fails, we don't have to block the user from using the app.
       * The best course of action is to return null, as if they were not
       * in the audience of the experiment.
       */
      return null;
    }
  };

  /**
   * Use this function to get a variant if the user has already been evaluated for the experiemnt.
   * @param experimentKey
   * @returns Promise of a variant string.
   */
  const getEvaluatedVariant = <K extends keyof ExperimentType>(experimentKey: K): ExperimentVariantUnion<K> | null => {
    const variant = experiment.variant(experimentKey);
    if (variant.value === undefined) {
      console.info(`Experiment ${experimentKey} cannot be found or turned off`);
      return null;
    }
    return variant.value as ExperimentVariantUnion<K>;
  };

  return (
    <ExperimentsContext.Provider
      value={{
        isStarted,
        fetchVariant,
        getEvaluatedVariant,
      }}
    >
      {children}
    </ExperimentsContext.Provider>
  );
};

export default ExperimentsContextProvider;
